import { atom, useAtomValue } from 'jotai';
import type { LocalMessage } from './conversation';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import getNimCacheId from './im/getNimCacheId';
import { tolgee } from 'services/i18n/tolgee';
import lastUsedConversation from 'utils/lastUsedConversation';
import { unstable_batchedUpdates } from 'react-dom';
import { useMemoizedFn } from 'ahooks';

const messageIdToConversationIdMap = new Map<
  Conversation.Message['msgId'],
  Conversation.Conversation['conversationId']
>();

const messageMapAtom = atom<Map<Conversation.Message['msgId'], LocalMessage>>(new Map());

const messageAtomFamily = atomFamily((msgId: Conversation.Message['msgId']) =>
  atom(
    (get) => get(messageMapAtom).get(msgId),
    (get, set, next: LocalMessage) => {
      const map = new Map(get(messageMapAtom));
      map.set(msgId, next);
      set(messageMapAtom, map);
      messageIdToConversationIdMap.set(msgId, next.conversationId);
    },
  ),
);

const recycledMessageAtomFamily = (msgId: Conversation.Message['msgId']) => {
  messageIdToConversationIdMap.delete(msgId);
  messageAtomFamily.remove(msgId);
};

lastUsedConversation.onRemove((removedConversationId) => {
  messageIdToConversationIdMap.forEach((conversationId, msgId) => {
    if (conversationId === removedConversationId) {
      recycledMessageAtomFamily(msgId);
    }
  });
});

messageAtomFamily.setShouldRemove((_, msgId: Conversation.Message['msgId']) => {
  const conversationId = messageIdToConversationIdMap.get(msgId);
  if (conversationId) {
    const shouldRemove = !lastUsedConversation.data.includes(conversationId);
    if (shouldRemove) {
      messageIdToConversationIdMap.delete(msgId);
    }
    return shouldRemove;
  }
  return true;
});

export const messageListState = atom((get) =>
  Array.from(get(messageMapAtom).values()).sort(
    (a, b) => parseInt(`${a.sendTime}`, 10) - parseInt(`${b.sendTime}`, 10),
  ),
);

export const useMessageList = () => useAtomValue(messageListState);

/**
 * FAQ_LIVEAGENT 按官方设定为普通买家消息
 * 展示形式与买家消息保持一致, 并支持引用
 */
const patchFAQLiveAgentMessage = (next: LocalMessage) => {
  if (next.messageType === 'FAQ_LIVEAGENT') {
    const text = tolgee.t('messageType.FAQ_LIVEAGENT');
    next.textContent = {
      text,
      originalText: text,
      translationContent: {},
    };
  }
};

const patchMergeNimCacheMessage = (
  next: LocalMessage,
  prev: LocalMessage | undefined,
  messageMap: Map<Conversation.Message['msgId'], LocalMessage>,
) => {
  const { msgId: nextMsgId } = next;
  const nimCacheId = getNimCacheId(next.yunXinMsgId);
  if (nextMsgId !== nimCacheId) {
    const nimCacheMessage = messageMap.get(nimCacheId);
    if (nimCacheMessage) {
      const { textContent, imageContent, videoContent, preview } = nimCacheMessage;
      next.textContent = textContent;
      next.imageContent = imageContent;
      next.videoContent = videoContent;
      next.preview = preview || prev?.preview;
      messageMap.delete(nimCacheId);
    }
  }
};
const patchPreview = (next: LocalMessage, prev: LocalMessage | undefined) => {
  if (prev?.preview && !next.preview) {
    next.preview = prev.preview;
  }
};

type NextValue = LocalMessage | ((prev: LocalMessage | undefined) => LocalMessage | undefined);

export const useUpdateMessage = () => {
  return useAtomCallback(
    useCallback((get, set, msgId: Conversation.Message['msgId'], next: NextValue) => {
      const messageMap = get(messageMapAtom);
      const prev = get(messageAtomFamily(msgId));
      const nextValue = typeof next === 'function' ? next(prev) : next;

      if (!nextValue) {
        return;
      }

      if (!lastUsedConversation.data.includes(nextValue.conversationId)) {
        return;
      }

      patchFAQLiveAgentMessage(nextValue);
      patchMergeNimCacheMessage(nextValue, prev, messageMap);
      patchPreview(nextValue, prev);

      set(messageAtomFamily(msgId), nextValue);
    }, []),
  );
};

export const useBatchUpdateMessages = () => {
  const updateMessage = useUpdateMessage();
  return useMemoizedFn((nextValues: [Conversation.Message['msgId'], NextValue][]) => {
    unstable_batchedUpdates(() => {
      nextValues.forEach(([msgId, nextValue]) => {
        updateMessage(msgId, nextValue);
      });
    });
  });
};

export const useGetMessageById = () =>
  useAtomCallback(
    useCallback((get, _, msgId: Conversation.Message['msgId']) => {
      return get(messageMapAtom).get(msgId);
    }, []),
  );

export const useDeleteMessage = () => {
  return useAtomCallback(
    useCallback((get, set, msgId: Conversation.Message['msgId']) => {
      const messageMap = get(messageMapAtom);
      if (messageMap.has(msgId)) {
        messageMap.delete(msgId);
        recycledMessageAtomFamily(msgId);
        set(messageMapAtom, new Map(messageMap));
      }
    }, []),
  );
};

/**
 * 原子化替换消息：删除旧消息并添加新消息，只触发一次渲染
 * 用于消息 ack 后将本地临时消息替换为服务端确认的消息
 */
export const useReplaceMessage = () => {
  return useAtomCallback(
    useCallback(
      (
        get,
        set,
        oldMsgId: Conversation.Message['msgId'],
        newMsgId: Conversation.Message['msgId'],
        next: LocalMessage,
      ) => {
        const messageMap = new Map(get(messageMapAtom));

        // 在删除前获取旧消息数据，用于后续合并 imageContent/videoContent/preview 等字段
        const oldMessage = messageMap.get(oldMsgId);

        // 删除旧消息
        if (oldMessage) {
          messageMap.delete(oldMsgId);
          recycledMessageAtomFamily(oldMsgId);
        }

        // 检查会话是否在活跃列表中
        if (!lastUsedConversation.data.includes(next.conversationId)) {
          set(messageMapAtom, messageMap);
          return;
        }

        // 应用消息补丁，传入旧消息数据以正确合并内容
        patchFAQLiveAgentMessage(next);
        patchMergeNimCacheMessage(next, oldMessage, messageMap);
        patchPreview(next, oldMessage);

        // 添加新消息
        messageMap.set(newMsgId, next);
        messageIdToConversationIdMap.set(newMsgId, next.conversationId);

        // 单次更新 atom，只触发一次渲染
        set(messageMapAtom, messageMap);
      },
      [],
    ),
  );
};
